import { Link } from 'react-router-dom';
import { Mail, Clock, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { usePendingReplies } from '@/hooks/useSecretary';
import { formatRelativeTime, formatEmailAddress } from '@/utils/formatters';

export default function PendingRepliesWidget() {
  const { data: pendingReplies, isLoading } = usePendingReplies();

  const emails = pendingReplies?.emails || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-500" />
            Pending Replies
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Emails awaiting your response
          </p>
        </div>
        {emails.length > 0 && (
          <Link
            to="/emails?reply_status=awaiting"
            className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            View all
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-12">
            <LoadingSpinner />
          </div>
        ) : emails.length > 0 ? (
          <div className="space-y-3">
            {emails.slice(0, 5).map((email) => {
              const replyByDate = email.reply_by_date ? new Date(email.reply_by_date) : null;
              const isOverdue = replyByDate && replyByDate < new Date();

              return (
                <Link
                  key={email.id}
                  to={`/emails?id=${email.id}`}
                  className={`block p-3 rounded-lg border transition-colors ${
                    isOverdue
                      ? 'bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-950/30'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {formatEmailAddress(email.from_address)}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {email.subject || '(No subject)'}
                      </p>

                      <div className="flex items-center gap-2 mt-2">
                        {replyByDate && (
                          <div className="flex items-center gap-1 text-xs">
                            <Clock
                              className={`h-3 w-3 ${
                                isOverdue ? 'text-red-500' : 'text-orange-500'
                              }`}
                            />
                            <span
                              className={
                                isOverdue
                                  ? 'text-red-600 dark:text-red-400 font-medium'
                                  : 'text-muted-foreground'
                              }
                            >
                              {isOverdue ? 'Overdue' : formatRelativeTime(replyByDate)}
                            </span>
                          </div>
                        )}
                        <Badge variant={isOverdue ? 'destructive' : 'warning'} className="text-xs">
                          Reply Needed
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No pending replies</p>
            <p className="text-xs text-muted-foreground mt-1">
              You're all caught up!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
